import { Logger } from '~~/helpers/logger';
import { useLocalization } from '../useLocalization';
import { errorToString } from '~~/helpers/error/errorToString';
import { INTERNAL_CMS_TYPE } from '~~/constants/cms';
import { useApiFetch } from '~/composables/useApiFetch';
import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import type { ComponentData } from '~~/types/cms/CmsComponentData';

type UseInternalCmsReturnType = {
  loadComponent: () => Promise<void>,
  loadPage: () => Promise<void>,
  internalCmsContent: Ref<ComponentData[]>,
  canShowContent: Ref<boolean>,
  loadingContent: Ref<boolean>,
};

export const useInternalCms = (slug: string): UseInternalCmsReturnType => {

  if (!slug) {
    
    throw new Error('useInternalCms: slug is required');
  
  }
  const { languageAndCountry } = useLocalization();
  const apiFetch = useApiFetch();
  const internalCmsContent: Ref<ComponentData[]> = useState(`${slug}-internalCmsContent`, () => []);
  const canShowContent = useState(`${slug}-canShowContent`, () => false);
  const loadingContent = useState(`${slug}-loadingContent`, () => false);

  const loadCmsContent = async (filePath: string, type: ValueOf<typeof INTERNAL_CMS_TYPE>) => {

    loadingContent.value = true;
    let fileContent: { content?: ComponentData } = {};
    try {

      fileContent = await apiFetch<{ content?: ComponentData }>(
        `${EXPONDO_MIDDLEWARE_ROUTES.ROOT}${EXPONDO_MIDDLEWARE_ROUTES.CMS.ROOT}/${filePath}`,
        {
          params: {
            locale: languageAndCountry.value,
            type,
          },
        },
      );
    
    } catch (error) {

      Logger.info('useInternalCms: fetch failed', {
        filePath,
        type,
        locale: languageAndCountry.value,
        error: errorToString(error),
      });
    
    }

    if (fileContent?.content) {

      internalCmsContent.value = [fileContent.content];
      canShowContent.value = true;

    } else {

      Logger.info('useInternalCms: empty content response', {
        filePath,
        type,
        locale: languageAndCountry.value,
      });

    }
    loadingContent.value = false;
  
  };

  const loadComponent = async () => {

    await loadCmsContent(slug, INTERNAL_CMS_TYPE.COMPONENTS);
  
  };

  const loadPage = async () => {

    await loadCmsContent(slug, INTERNAL_CMS_TYPE.PAGES);
  
  };

  return {
    loadComponent,
    loadPage,
    internalCmsContent,
    canShowContent,
    loadingContent,
  };

};
